import { ApiError } from "./apiError";
import { navigateTo } from "./navigation";
import { termsAcknowledgement } from "./termsAcknowledgement";

const TERMS_ACKNOWLEDGMENT_REQUIRED = "terms_acknowledgment_required";

// Guard so the terms endpoints themselves can never re-enter the terms flow.
const isTermsEndpointError = (error: ApiError): boolean =>
  typeof error.url === "string" && error.url.includes("/api/v1/terms/");

const isTermsAcknowledgmentRequired = (error: ApiError): boolean =>
  error.body?.code === TERMS_ACKNOWLEDGMENT_REQUIRED;

export async function retryWithAuth<T>(
  fn: () => Promise<T>,
  retries: number = 1,
  options?: {
    redirectOnNotFound?: boolean;
    redirectOnForbidden?: boolean;
    redirectOnServerError?: boolean;
  }
): Promise<T> {
  let attempts = 0;
  let termsRetried = false;
  const redirectOnNotFound = options?.redirectOnNotFound ?? true;
  const redirectOnForbidden = options?.redirectOnForbidden ?? true;
  const redirectOnServerError = options?.redirectOnServerError ?? true;

  while (attempts <= retries) {
    try {
      return await fn();
    }
    catch (error) {
      if (!(error instanceof ApiError)) {
        throw error;
      }

      if (error.isBadRequest()) {
        console.warn("400 → bad request");
        throw error; // component handelt dit af
      }

      // 🔁 401 → refresh token
      if (error.isUnauthorized()) {
        attempts++;

        if (attempts > retries) {
          console.warn("401 → redirect to login");
          navigateTo("/verlopen");
          throw error;
        }

        continue;
      }

      // 403
      if (error.isForbidden()) {
        // New terms must be accepted: open the blocking dialog instead of the
        // "niet-toegestaan" page, then retry the original request once.
        if (!isTermsEndpointError(error) && isTermsAcknowledgmentRequired(error)) {
          if (termsRetried) {
            throw error;
          }
          termsRetried = true;
          await termsAcknowledgement.require();
          continue;
        }

        if (redirectOnForbidden) {
          navigateTo("/niet-toegestaan");
        }
        throw error;
      }

      // 404
      if (error.isNotFound()) {
        if (redirectOnNotFound) {
          navigateTo("/niet-gevonden");
        }
        throw error;
      }

      // 422 → validation → laat component beslissen
      if (error.isValidation()) {
        throw error;
      }

      // 500
      if (error.isServerError()) {
        // if (redirectOnServerError) {
        //   navigateTo("/server-fout");
        // }
        throw error;
      }

      throw error;
    }
  }

  throw new Error("Retry failed unexpectedly");
}
